import {
  chatServerMessageSchema,
  type AgentStatus,
  type ChatClientMessage,
  type ChatMessage,
  type ChatServerMessage,
  type ChatTypingUser,
} from '@hub/contracts'
import { buildWsRoomPath, getWsBaseUrl } from '@/lib/collab/ws-url'

/**
 * Client for a ChatStore thread (PLAN-082). Owns the socket, the message
 * list (a Map by id, ordered by seq), the agent status, typing, an
 * IndexedDB copy for instant reopen, and older-history paging. Writes go
 * through procedures; the only things sent here are `typing` and `history`.
 * Framework-free; `useChatCollab` wraps it. Instances are pooled (see
 * `chat-pool.ts`) so revisits reuse the open socket.
 */

export type ChatStatus = 'connecting' | 'connected' | 'disconnected'

export interface ChatClientEvents {
  /** Message list changed (any reason). */
  messages: (messages: ChatMessage[]) => void
  status: (status: AgentStatus) => void
  typing: (users: ChatTypingUser[]) => void
  connection: (status: ChatStatus) => void
  /** Older-history availability changed. */
  history: (hasMore: boolean, loading: boolean) => void
}

const CACHE_DB = 'hub-chats'
const CACHE_STORE = 'snapshots'
const RECONNECT_BASE_MS = 500
const RECONNECT_MAX_MS = 10_000

type Cached = { rev: number; messages: ChatMessage[]; hasMore: boolean }

function openCache(): Promise<IDBDatabase | null> {
  if (typeof indexedDB === 'undefined') return Promise.resolve(null)
  return new Promise((resolve) => {
    try {
      const req = indexedDB.open(CACHE_DB, 1)
      req.onupgradeneeded = () => req.result.createObjectStore(CACHE_STORE)
      req.onsuccess = () => resolve(req.result)
      req.onerror = () => resolve(null)
    } catch {
      resolve(null)
    }
  })
}

async function cacheRead(key: string): Promise<Cached | null> {
  const db = await openCache()
  if (!db) return null
  return new Promise((resolve) => {
    try {
      const req = db.transaction(CACHE_STORE, 'readonly').objectStore(CACHE_STORE).get(key)
      req.onsuccess = () => resolve((req.result as Cached | undefined) ?? null)
      req.onerror = () => resolve(null)
    } catch {
      resolve(null)
    }
  })
}

async function cacheWrite(key: string, value: Cached): Promise<void> {
  const db = await openCache()
  if (!db) return
  try {
    db.transaction(CACHE_STORE, 'readwrite').objectStore(CACHE_STORE).put(value, key)
  } catch {
    // quota or private mode — the cache is a convenience
  }
}

export async function forgetChatCache(projectId: string, threadId: string): Promise<void> {
  const db = await openCache()
  if (!db) return
  try {
    db.transaction(CACHE_STORE, 'readwrite')
      .objectStore(CACHE_STORE)
      .delete(`${projectId}:${threadId}`)
  } catch {
    // ignore
  }
}

/** Logout: drop every cached thread of every project. */
export async function clearChatCache(): Promise<void> {
  if (typeof indexedDB === 'undefined') return
  try {
    indexedDB.deleteDatabase(CACHE_DB)
  } catch {
    // ignore
  }
}

export class ChatClient {
  readonly projectId: string
  readonly threadId: string
  #messages = new Map<string, ChatMessage>()
  #sorted: ChatMessage[] | null = null
  agentStatus: AgentStatus = { state: 'idle' }
  typing: ChatTypingUser[] = []
  connection: ChatStatus = 'connecting'
  /** True once a server snapshot has been applied (cache alone is not "synced"). */
  synced = false
  /** True once anything renderable exists (cache or server). */
  ready = false
  hasMore = false
  loadingHistory = false
  self: string | null = null

  #ws: WebSocket | null = null
  #closed = false
  #reconnectAttempt = 0
  #reconnectTimer: ReturnType<typeof setTimeout> | null = null
  #snapshotBuffer: ChatMessage[] = []
  #cacheTimer: ReturnType<typeof setTimeout> | null = null
  #rev = 0
  #typingSent = false
  #listeners: { [K in keyof ChatClientEvents]: Set<ChatClientEvents[K]> } = {
    messages: new Set(),
    status: new Set(),
    typing: new Set(),
    connection: new Set(),
    history: new Set(),
  }

  constructor(projectId: string, threadId: string) {
    this.projectId = projectId
    this.threadId = threadId
  }

  get cacheKey() {
    return `${this.projectId}:${this.threadId}`
  }

  /** Messages ordered by seq (optimistic ones, seq -1, go last). */
  get messages(): ChatMessage[] {
    if (!this.#sorted) {
      this.#sorted = [...this.#messages.values()].sort((a, b) => {
        const as = a.seq < 0 ? Number.MAX_SAFE_INTEGER : a.seq
        const bs = b.seq < 0 ? Number.MAX_SAFE_INTEGER : b.seq
        return as - bs || a.createdAt.localeCompare(b.createdAt)
      })
    }
    return this.#sorted
  }

  on<K extends keyof ChatClientEvents>(event: K, fn: ChatClientEvents[K]): () => void {
    this.#listeners[event].add(fn)
    return () => this.#listeners[event].delete(fn)
  }

  #emit<K extends keyof ChatClientEvents>(event: K, ...args: Parameters<ChatClientEvents[K]>) {
    for (const fn of this.#listeners[event]) (fn as (...a: unknown[]) => void)(...args)
  }

  #changed() {
    this.#sorted = null
    this.ready = true
    this.#emit('messages', this.messages)
  }

  async connect(): Promise<void> {
    this.#closed = false
    const cached = await cacheRead(this.cacheKey)
    if (this.#closed) return
    if (cached && !this.synced && this.#messages.size === 0) {
      for (const m of cached.messages) this.#messages.set(m.id, m)
      this.hasMore = cached.hasMore
      this.#changed()
      this.#emit('history', this.hasMore, false)
    }
    this.#open()
  }

  #open() {
    if (this.#closed) return
    this.#setConnection('connecting')
    const url = `${getWsBaseUrl()}/${buildWsRoomPath(this.projectId, 'chat', this.threadId)}`
    const ws = new WebSocket(url)
    this.#ws = ws
    ws.onopen = () => {
      this.#reconnectAttempt = 0
      this.#setConnection('connected')
      if (this.#typingSent) this.#sendRaw({ t: 'typing', typing: true })
    }
    ws.onmessage = (ev) => {
      let msg: ChatServerMessage
      try {
        msg = chatServerMessageSchema.parse(JSON.parse(String(ev.data)))
      } catch (err) {
        console.error('[chat-client] bad server message', err)
        return
      }
      this.#handle(msg)
    }
    ws.onclose = (ev) => {
      if (this.#ws === ws) this.#ws = null
      this.#setConnection('disconnected')
      if (ev.code === 1001 && /deleted/.test(ev.reason)) {
        this.#closed = true
        return
      }
      this.#scheduleReconnect()
    }
    ws.onerror = () => {
      // onclose follows
    }
  }

  #scheduleReconnect() {
    if (this.#closed || this.#reconnectTimer) return
    const delay = Math.min(RECONNECT_MAX_MS, RECONNECT_BASE_MS * 2 ** this.#reconnectAttempt++)
    this.#reconnectTimer = setTimeout(() => {
      this.#reconnectTimer = null
      this.#open()
    }, delay)
  }

  close() {
    this.#closed = true
    if (this.#reconnectTimer) clearTimeout(this.#reconnectTimer)
    this.#reconnectTimer = null
    if (this.#cacheTimer) {
      clearTimeout(this.#cacheTimer)
      this.#cacheTimer = null
      this.#writeCache()
    }
    this.#ws?.close(1000, 'closed')
    this.#ws = null
  }

  #setConnection(status: ChatStatus) {
    if (this.connection === status) return
    this.connection = status
    this.#emit('connection', status)
  }

  #sendRaw(msg: ChatClientMessage) {
    if (this.#ws?.readyState === WebSocket.OPEN) this.#ws.send(JSON.stringify(msg))
  }

  #handle(msg: ChatServerMessage) {
    switch (msg.t) {
      case 'snapshot': {
        if (msg.chunk.i === 0) {
          this.#snapshotBuffer = []
          if (msg.self) this.self = msg.self
          if (msg.hasMore !== undefined) this.hasMore = msg.hasMore
          if (msg.agentStatus) this.#setStatus(msg.agentStatus)
          if (msg.typing) {
            this.typing = msg.typing
            this.#emit('typing', msg.typing)
          }
        }
        this.#snapshotBuffer.push(...msg.messages)
        if (msg.chunk.i < msg.chunk.n - 1) return
        // Keep older pages we already loaded and any optimistic echo still
        // waiting for its row; everything else is what the server holds.
        const oldest = this.#snapshotBuffer[0]?.seq ?? Number.MAX_SAFE_INTEGER
        const next = new Map<string, ChatMessage>()
        for (const m of this.#messages.values()) {
          if (m.seq < 0 || m.seq < oldest) next.set(m.id, m)
        }
        for (const m of this.#snapshotBuffer) next.set(m.id, m)
        this.#messages = next
        this.#snapshotBuffer = []
        this.#rev = msg.rev
        this.synced = true
        this.#changed()
        this.#emit('history', this.hasMore, this.loadingHistory)
        this.#scheduleCacheWrite()
        break
      }
      case 'history': {
        for (const m of msg.messages) this.#messages.set(m.id, m)
        this.hasMore = msg.hasMore
        this.loadingHistory = false
        this.#changed()
        this.#emit('history', this.hasMore, false)
        break
      }
      case 'message': {
        // The server row for our optimistic echo replaces it under the same id.
        this.#messages.set(msg.message.id, msg.message)
        this.#rev = msg.rev
        this.#changed()
        this.#scheduleCacheWrite()
        break
      }
      case 'delta': {
        const current = this.#messages.get(msg.id)
        if (!current) break
        const next: ChatMessage = { ...current }
        if (msg.content !== undefined) next.content = msg.content
        else if (msg.append) next.content = current.content + msg.append
        if (msg.steps) next.steps = msg.steps
        if (msg.artifacts) next.artifacts = msg.artifacts
        if (msg.citations) next.citations = msg.citations
        if (msg.usage) next.usage = msg.usage
        if (msg.error !== undefined) next.error = msg.error ?? undefined
        if (msg.completedAt) next.completedAt = msg.completedAt
        this.#messages.set(msg.id, next)
        this.#rev = msg.rev
        this.#changed()
        if (msg.completedAt) this.#scheduleCacheWrite()
        break
      }
      case 'deleted': {
        if (msg.mode === 'hard') this.#messages.delete(msg.id)
        else {
          const current = this.#messages.get(msg.id)
          if (current)
            this.#messages.set(msg.id, {
              ...current,
              deletedAt: msg.deletedAt,
              deletedBy: msg.deletedBy,
            })
        }
        this.#rev = msg.rev
        this.#changed()
        this.#scheduleCacheWrite()
        break
      }
      case 'status':
        this.#setStatus(msg.agentStatus)
        break
      case 'typing':
        this.typing = msg.typing
        this.#emit('typing', msg.typing)
        break
      case 'error':
        console.error('[chat-client]', msg.message)
        break
    }
  }

  #setStatus(status: AgentStatus) {
    this.agentStatus = status
    this.#emit('status', status)
  }

  #scheduleCacheWrite() {
    if (this.#cacheTimer) return
    this.#cacheTimer = setTimeout(() => {
      this.#cacheTimer = null
      this.#writeCache()
    }, 1_000)
  }

  #writeCache() {
    const messages = this.messages.filter((m) => m.seq >= 0)
    void cacheWrite(this.cacheKey, { rev: this.#rev, messages, hasMore: this.hasMore })
  }

  // ── local mutations (the server confirms or replaces) ─────────────────

  /** Show the user's message right away; `triggerAgent` stores it under the same id. */
  addOptimistic(message: Omit<ChatMessage, 'seq'>): void {
    this.#messages.set(message.id, { ...message, seq: -1 })
    this.#changed()
  }

  /** Drop an optimistic echo whose `triggerAgent` failed. */
  removeOptimistic(id: string): void {
    const m = this.#messages.get(id)
    if (m && m.seq < 0) {
      this.#messages.delete(id)
      this.#changed()
    }
  }

  /** Mirror a local delete immediately; the `deleted` broadcast confirms it. */
  applyLocalDelete(id: string, mode: 'soft' | 'hard', by?: string): void {
    const current = this.#messages.get(id)
    if (!current) return
    if (mode === 'hard') this.#messages.delete(id)
    else this.#messages.set(id, { ...current, deletedAt: new Date().toISOString(), deletedBy: by })
    this.#changed()
  }

  applyLocalStatus(status: AgentStatus): void {
    this.#setStatus(status)
  }

  setTyping(typing: boolean): void {
    if (this.#typingSent === typing) return
    this.#typingSent = typing
    this.#sendRaw({ t: 'typing', typing })
  }

  loadOlder(limit?: number): void {
    if (!this.hasMore || this.loadingHistory) return
    const oldest = this.messages.find((m) => m.seq >= 0)
    if (!oldest) return
    this.loadingHistory = true
    this.#emit('history', this.hasMore, true)
    this.#sendRaw({ t: 'history', beforeSeq: oldest.seq, limit })
  }
}
